/* eslint-disable @next/next/no-img-element */
/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { gsColors } from '../../../../styles/Variables'
import ArrowButton from '../../../ArrowButton'
import { useBooleanState, formatCurrency, mq, useIsDesktop, trackError } from '../../../../helpers'
import SelectedServices from './SelectedServices'
import { Header3 } from '../../../../styles/Components'

const BookingStepFooter = styled.footer`
  background-color: ${gsColors.white};
  box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.15);
  flex: 0 0 auto;
  position: fixed;
  opacity: 1;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;

  .container {
    opacity: 1;
    background-color: #FFF;
    margin: 0 auto;
    max-width: 1600px;

    ${mq('min', '768px')} {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .summary {
    height: 80px;
    position: relative;
    width: 500px;
    opacity: 1;
    background-color: #FFF;

    ${mq('max', '768px')} {
      height: unset;
      width: 100%;
      margin-right: 20px;
    }

    
    .cart-button {
      cursor: pointer;
      display: flex;
      align-items: center;
      border: none;
      background-color: #FFF;
      height: 100%;
      padding: 0 32px;
      z-index: 1000;
      
      img {
        margin-right: 12px;
      }

      ${mq('max', '768px')} {
        height: unset;
        padding: 16px 0;
        width: 100%;
        justify-content: center;

        h3 {
          font-size: 15px !important;
        }
      }
      
    }
    
    .cart {
      z-index: -500;
      box-shadow: 0px -10px 10px -10px rgba(0, 0, 0, 0.15),
        -10px 0px 10px -10px rgba(0, 0, 0, 0.15),
        10px 0px 10px -10px rgba(0, 0, 0, 0.15);
      position: absolute;
      bottom: -2000px;
      left: 20px;
      background-color: #FFF;
      min-width: 393px;
      transition: all .2s ease;
      
      .cart-inner {
        display: none;
      }

      ${mq('max', '768px')} {
        width: 95%;
        left: 50%;
        transform: translateX(-50%);
      }

      &.open {
        transition: all .2s ease;
        bottom: 80px;

        ${mq('max', '768px')} {
          bottom: 56px;
        }

        .cart-inner {
          display: flex;
          z-index: 500;
        }
      }

    }
  }

  .appointment-total {
    display: flex;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.22;
    font-family: Euclid;
    justify-content: space-between;
    margin: 20px 15px;

    ${mq('min', '768px')} {
      font-size: 21px;
    }
  }
`


const BookingFlowFooter = ({
  state: { selectedAppointment: appointment, step, serviceRequests, market },
  next,
  nextButtonLabel,
  nextButtonEnabled,
  deselectMarketService,
  dispatchValueAtKeyPath
}) => {
  const isDesktop = useIsDesktop()

  const [summaryVisible, setSummaryVisibility] = useBooleanState()
  const [working, setWorking] = useState(false)
  const [submitError, setSubmitError] = useState()

  useEffect(() => {
    const clickAway = (e) => {
      const ids = [
        e.target.id,
        e.target?.parentElement?.id,
        e.target?.parentElement?.parentElement?.id,
      ]
  
      if (ids.includes('cart-button')) {
        return
      }
  
      if (
        summaryVisible &&
        !ids.includes('cart-list') &&
        !ids.includes('cart-remove')
      ) {
        setSummaryVisibility()
      }
    }
    window.addEventListener('click', clickAway, { passive: true })
  
    return () => window.removeEventListener('click', clickAway)
  }, [summaryVisible, setSummaryVisibility])  


  const submit = async () => {
    try {
      setWorking(true)
      setSubmitError(null)
      await next()
    } catch (err) {
      trackError('Booking Error', err)
      setSubmitError(err.message?.replace(/^.*?: /, '') || 'An error occurred')
    } finally {
      setWorking(false)
    }
  }

  const goToStep2 = () => {
    dispatchValueAtKeyPath('step', 2)
    setSummaryVisibility()
  }

  const isStepWithoutFooter = () => [1, 4, 6].includes(step)

  const hideOnDesktop = () => isDesktop && [1].includes(step)

  if (isStepWithoutFooter() || hideOnDesktop()) {
    return null
  }

  return (
    <BookingStepFooter>
      <div className="container">
        {/* container is justify-content: space-between; placeholder div keeps next button in place */}
        {!serviceRequests.length  && <div />}

        {!!serviceRequests.length && step < 5 && (
          <div className={`summary ${summaryVisible ? 'open' : ''}`}>
            <button className='cart-button' onClick={setSummaryVisibility} id='cart-button'>
              <img src='/images/book-calendar-icon.svg' alt='calendar' />
              <Header3
                fontSize={18}
                spacing={1.44}
                color={gsColors.sunsetOrange}
              >
                My Appointment ({serviceRequests.length})
              </Header3>
            </button>
            <div className={`cart ${summaryVisible ? 'open' :  ''}`}>
              <div className='cart-inner'>
                <SelectedServices
                  {...{
                    step,
                    deselectMarketService,
                    goToStep2,
                    services: serviceRequests,
                    market
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {step === 5 && (
          <div className="appointment-total">
            {isDesktop ? (
              <span>
                Total: {formatCurrency(appointment.finalPriceWithTaxAndTip)}
                {/* // TODO: Total is missing from availability response! */}
              </span>
            ) : (
              <>
                <span>Total</span>
                <span>
                  {formatCurrency(appointment.finalPriceWithTaxAndTip)}
                </span>
              </>
            )}
          </div>
        )}
        {submitError && (
          <div className="book-error">
            <span>{submitError}</span>
          </div>
        )}
        <ArrowButton
          footer
          disabled={!nextButtonEnabled || working}
          onClick={submit}
        >
          {nextButtonLabel}
        </ArrowButton>
      </div>
    </BookingStepFooter>
  )
}

export default BookingFlowFooter
